import CheckBox from '@app/components/CheckBox'
import { FileDropzone, FileProgress } from '@app/components/FileUpload'
import MentionEditor from '@app/components/MentionEditor'
import { FeedItemType, LeadPriority } from '@app/models/enums'
import { IFeedItem } from '@app/models/FeedItem'
import { bindFunctions } from '@app/utils/class'
import { IconName } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { EditorState } from 'draft-js'
import * as React from 'react'
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  UncontrolledDropdown,
} from 'reactstrap'

interface IFeedItemEditBodyProps {
  item: IFeedItem
  cancelEdit: (feedItemId: string) => void
  onLayoutChanged?: () => void
  saveEdit: (feedItem: IFeedItem) => void
  setEditIsPublic: (feedItemId: string, isPublic: boolean) => void
  setEditLeadPriority: (feedItemId: string, value: string) => void
  setEditLeadType: (feedItemId: string, value: string) => void
  setEditMarkup: (feedItemId: string, markupState: EditorState) => void
  setEditTitle: (feedItemId: string, title: string) => void
}

export default class FeedItemEditBody extends React.PureComponent<
  IFeedItemEditBodyProps
> {
  constructor(props: IFeedItemEditBodyProps) {
    super(props)

    bindFunctions(this)
  }

  public render() {
    const editState = this.props.item.editState

    if (!editState) {
      return null
    }

    const dropzoneStyle: React.CSSProperties = {
      borderWidth: '0px',
      width: '100%',
      padding: '0px',
    }

    const selectPhotoStyle: React.CSSProperties = {
      borderWidth: '0px',
      padding: '0px',
    }

    let priorityIconName: IconName
    let iconClasses: string | undefined
    priorityIconName = 'minus-circle'

    switch (editState.leadPriority) {
      case LeadPriority.High:
        iconClasses = 'text-danger'
        priorityIconName = 'exclamation-circle'
        break

      case LeadPriority.Low:
        iconClasses = 'text-info'
        priorityIconName = 'arrow-alt-circle-down'
        break

      default:
        priorityIconName = 'minus-circle'
        break
    }

    return (
      <div className='feed-item-edit-body'>
        {this.props.item.type === FeedItemType.Lead && (
          <div className='feed-item-edit-title input-group'>
            <div className='input-group-prepend'>
              <UncontrolledDropdown>
                <DropdownToggle
                  color='light'
                  style={{ borderRadius: 0 }}
                  className='align-items-center'
                >
                  <FontAwesomeIcon
                    className={`mr-2 ${iconClasses}`}
                    icon={['fal', priorityIconName]}
                  />
                  <FontAwesomeIcon
                    style={{ fontSize: '0.8rem' }}
                    icon={['fas', 'caret-down']}
                    className='mr-2'
                  />
                </DropdownToggle>
                {this._renderDropdown()}
              </UncontrolledDropdown>
            </div>
            <Input
              className='input-no-border'
              onChange={this._onChangeTitle}
              placeholder='Title'
              required
              style={{ borderRight: '1px solid #ddd' }}
              value={editState.title || ''}
            />

            <div className='input-group-append'>
              <Input
                className='input-no-border'
                type='select'
                value={editState.leadType}
                onChange={this._onChangeLeadType}
              >
                <option value='BuyerUser'>Buyer / User</option>
                <option value='BuyerInvestor'>Buyer / Investor</option>
                <option value='DevelopmentClient'>Development Client</option>
                <option value='Other'>Other</option>
                <option value='Seller'>Seller</option>
                <option value='Tenant'>Tenant</option>
              </Input>
            </div>
          </div>
        )}
        <div>
          <FileDropzone
            identifier={this.props.item.id}
            disableClick={true}
            style={dropzoneStyle}
          >
            <MentionEditor
              compact
              id={this.props.item.id}
              editorState={editState.markupState}
              onChange={this._onChangeMarkup}
            />
          </FileDropzone>
          <FileProgress
            identifier={this.props.item.id}
            dataSource={this.props.item.attachments}
            onResize={this.props.onLayoutChanged}
          />
        </div>
        <div className='feed-item-edit-footer d-flex p-2 justify-content-between align-items-center'>
          <div>
            <CheckBox
              className='text-white'
              value={editState.isPublic}
              onChange={this._onChangeIsPublic}
            >
              Make public
            </CheckBox>
          </div>
          <div>
            <div className='feed-item-buttons'>
              <Button
                color='link'
                className='mr-2'
                disabled={this.props.item.isSaving}
                onClick={this._cancelEdit}
                size='sm'
              >
                Cancel
              </Button>
            </div>

            <div className='feed-item-buttons'>
              <FileDropzone
                identifier={this.props.item.id}
                style={selectPhotoStyle}
              >
                <Button color='primary' className='mr-2' size='sm'>
                  <FontAwesomeIcon
                    icon={['fal', 'camera']}
                    className='feed-item-camera'
                  />
                </Button>
              </FileDropzone>
            </div>

            <div className='feed-item-buttons'>
              <Button
                color='primary'
                disabled={this.props.item.isSaving}
                onClick={this._saveEdit}
                size='sm'
              >
                Update
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  private _renderDropdown() {
    const items = []

    items.push(
      <DropdownItem
        onClick={() =>
          this.props.setEditLeadPriority(this.props.item.id, 'High')
        }
        key='high'
      >
        <FontAwesomeIcon
          className='mr-2 text-danger'
          icon={['fal', 'exclamation-circle']}
        />
        High
      </DropdownItem>,
    )

    items.push(
      <DropdownItem
        onClick={() =>
          this.props.setEditLeadPriority(this.props.item.id, 'Medium')
        }
        key='medium'
      >
        <FontAwesomeIcon className='mr-2' icon={['fal', 'minus-circle']} />
        Medium
      </DropdownItem>,
    )

    items.push(
      <DropdownItem
        onClick={() =>
          this.props.setEditLeadPriority(this.props.item.id, 'Low')
        }
        key='low'
      >
        <FontAwesomeIcon
          className='mr-2 text-info'
          icon={['fal', 'arrow-alt-circle-down']}
        />
        Low
      </DropdownItem>,
    )

    return <DropdownMenu>{items}</DropdownMenu>
  }

  private _cancelEdit() {
    this.props.cancelEdit(this.props.item.id)
  }

  private _onChangeIsPublic(newIsPublic: boolean) {
    this.props.setEditIsPublic(this.props.item.id, newIsPublic)
  }

  private _onChangeLeadType(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setEditLeadType(this.props.item.id, event.target.value)
  }

  private _onChangeMarkup(newMarkupState: EditorState) {
    this.props.setEditMarkup(this.props.item.id, newMarkupState)
  }

  private _onChangeTitle(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setEditTitle(this.props.item.id, event.target.value)
  }

  private _saveEdit() {
    this.props.saveEdit(this.props.item)
  }
}
